import { useEffect, useState } from 'react';
import { redirect } from '@tanstack/react-router';
import { DateTime, DurationLike } from 'luxon';

import { USER_ROLE, ACCESS_TOKEN_KEY } from '@/constants';
import {
	AUTH_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	parseJwt,
} from '@shared/auth/Authentication.hooks';
import config from '@/config';
import { AuthCognito } from '@/config.type';
import { Logger } from '@shared/lib/Logger';
import { msToTime } from './format';
import { clearTokens, setAccessToken } from './tokens';
import { notifyAuthStateChange } from './authState';

/**
 * The Cognito authorization-code flow used to be forked, near-identically,
 * between `features/login/Auth.hooks.tsx` and
 * `templates/magshop/auth/Auth.hooks.tsx` (the former also backs `daily`,
 * `magazine`/smartmags, and `starmagazine`, which import `features/login`'s
 * copy directly). This factory holds the one shared implementation; every real
 * behavioural difference the two forks had is captured explicitly below as
 * `CognitoAuthOptions` rather than silently unified, so each template's
 * live tenants (`magshop`, `scubadiver`, `theog`, `daily`, `smartmags`,
 * `starmagazine` per `pwa-config`) keep exactly the behaviour they had
 * before this consolidation.
 */
export interface CognitoAuthOptions {
	/** Cognito hosted-UI path for the initial login redirect. basic: '/login'. magshop: '/authorize'. */
	authorizePath: string;
	/** OAuth scope for the initial redirect. basic: 'email+openid'. magshop: 'email+openid+profile'. */
	scope: string;
	/** Backend path that exchanges a refresh_token for a fresh access_token. basic: '/auth/token'. magshop: '/a360/magshop/token'. */
	refreshTokenPath: string;
	/**
	 * Hook computing the `/callback` redirect_uri path segment. Kept as an
	 * injected hook (not unified) because basic and magshop used different
	 * router APIs to compute it (`router.basepath` vs `useLinkProps`) —
	 * preserved as-is rather than assumed equivalent.
	 */
	useBasename: () => string;
	/** magshop sets AUTH_KEY='true' for an authenticated admin; basic leaves it untouched. */
	setAuthKeyForAdmin: boolean;
	/** magshop treats missing access/refresh *expiration* keys alone as an invalid session (both in `useAuth` and `tryRefreshToken`); basic only checks the tokens themselves. */
	requireExpirationKeysPresent: boolean;
	/**
	 * In `useAuth`'s effect, which source of truth basic vs magshop used for
	 * token expiration dates: basic ('jwt') always parsed the token's own
	 * `exp` claim via `parseJwt`, never reading the `*_EXPIRATION_KEY`
	 * localStorage values at all. magshop ('storedIso') never called
	 * `parseJwt` here — it trusted the stored ISO strings directly (safe
	 * only because `requireExpirationKeysPresent: true` already guarantees
	 * they're present by this point). Not the same value in general — do
	 * not merge into a single "prefer stored, fall back to JWT" branch.
	 */
	expirationSource: 'jwt' | 'storedIso';
	/** magshop clears tokens (forcing re-login) when the background refresh in useAuth's effect fails; basic only logs and leaves the stale session in place. */
	clearTokensOnRefreshFailureInAuthEffect: boolean;
	/**
	 * magshop's `tryRefreshToken` (the periodic background check) actually
	 * calls `refreshAccessToken` when the access token is expired. basic's
	 * never did — it only logs, so its periodic check is a no-op; basic's
	 * *only* live refresh path is `useAuth`'s own effect. Preserved as-is:
	 * this is dead-code-shaped, not a bug being reproduced blind — it was
	 * verified basic's `<Auth>` wrapper (which mounts `useAuth`) isn't
	 * rendered anywhere either, so for basic-family tenants neither path is
	 * currently exercised by anything in this app; access is gated
	 * externally and this app only handles the callback/refresh/logout ends.
	 */
	tryRefreshTokenPerformsRefresh: boolean;
	/** magshop's `tryRefreshToken` clears tokens on any invalid-session case it detects (missing tokens, expired refresh, failed refresh); basic's never clears. */
	tryRefreshTokenClearsOnInvalidSession: boolean;
	/** Builds the Cognito logout URL. Differs in query params and whether it's gated on `config.app.auth.type === 'cognito'`. */
	buildLogoutUrl: (redirectUri: string) => string | null;
	/**
	 * basic's original `useAuth()` returned — before even calling
	 * `useEffect` — when `config.app.auth.type !== 'cognito'`; magshop's
	 * never checked. Safe as a plain conditional return here (not a rules-
	 * of-hooks violation in practice) because `config.app.auth.type` is a
	 * per-build constant baked in at build time, never changing across
	 * renders of a running app instance.
	 */
	requireCognitoAuthType: boolean;
	/**
	 * `ensureAuthenticated` (the `beforeLoad` guard) only enforces anything
	 * when `config.app.auth.enabled` is true. This is what makes it safe to
	 * wire up on `basic`/`magazine`, where most tenants ship with Cognito
	 * *configured* but not *enabled* — without this check, a `beforeLoad`
	 * guard would newly lock those tenants' Home/Issue routes behind a
	 * login wall they never asked for.
	 */
	requireAuthEnabled: boolean;
}

export function createCognitoAuth(options: CognitoAuthOptions) {
	const logger = new Logger('Token', {
		log: 'rgb(88, 86, 214)',
		debug: 'rgb(54, 52, 163)',
		info: 'rgb(125, 122, 255)',
		error: 'rgb(211, 15, 69)',
	});

	function authorizeUrl(redirectUri: string): string {
		const auth = config.app.auth as AuthCognito;
		const sections = [
			`client_id=${auth.config.clientId}`,
			'response_type=code',
			`scope=${options.scope}`,
			`redirect_uri=${redirectUri}`,
		].join('&');

		return [auth.config.host, `${options.authorizePath}?`, sections].join('');
	}

	async function refreshAccessToken(token: string): Promise<string> {
		const queryParams = new URLSearchParams({
			refresh_token: token,
		}).toString();
		const url = `${config.build.host}${options.refreshTokenPath}?${queryParams}`;
		const rawResponse = await fetch(url, {
			method: 'GET',
			headers: {
				'app-key': config.build.appKey,
				'Content-Type': 'application/json',
			},
		});
		const content = await rawResponse.json();
		const { access_token, expires } = content;

		const accessExpirationDate = DateTime.now()
			.plus({ seconds: expires })
			.toISO();

		setAccessToken(access_token, `${accessExpirationDate}`);
		return `${accessExpirationDate}`;
	}

	/**
	 * The actual auth-check logic, shared between `useAuth`'s effect (below)
	 * and `ensureAuthenticated` (the `beforeLoad` guard). Extracted so the
	 * branching only exists once — this session already found three real
	 * behavioural bugs from having it duplicated once; not risking a second
	 * copy for `beforeLoad`. Returns an instruction rather than mutating
	 * state directly, so each caller can act on it its own way (React state
	 * + `location.href` for `useAuth`, a thrown router `redirect()` for
	 * `ensureAuthenticated`).
	 */
	async function checkAuth(
		isAdmin: boolean,
		isAuthenticated: boolean,
		redirectUrl: string,
	): Promise<CognitoAuthCheckResult> {
		if (isAdmin) {
			if (options.setAuthKeyForAdmin) {
				localStorage.setItem(AUTH_KEY, 'true');
				notifyAuthStateChange();
			}
			return { allowed: true };
		}

		if (!isAuthenticated) {
			return { allowed: false, redirectUrl };
		}

		// At this point we know that the user is authenticated
		const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		const accessTokenExp = localStorage.getItem(ACCESS_TOKEN_EXPIRATION_KEY);
		const refreshTokenExp = localStorage.getItem(
			REFRESH_TOKEN_EXPIRATION_KEY,
		);

		const missingSession = options.requireExpirationKeysPresent
			? !accessToken || !refreshToken || !accessTokenExp || !refreshTokenExp
			: !accessToken || !refreshToken;

		if (missingSession) {
			clearTokens();
			return { allowed: false, redirectUrl };
		}

		// In here the access token is expired but the app is offline
		if (!navigator.onLine) {
			return { allowed: true };
		}

		let accessExpirationDate: DateTime;
		let refreshExpirationDate: DateTime;
		if (options.expirationSource === 'jwt') {
			const accessTokenData = parseJwt(accessToken as string);
			const refreshTokenData = parseJwt(refreshToken as string);
			accessExpirationDate = DateTime.fromSeconds(accessTokenData.exp);
			refreshExpirationDate = DateTime.fromSeconds(refreshTokenData.exp);
		} else {
			// Safe: requireExpirationKeysPresent must be true whenever
			// expirationSource is 'storedIso', so missingSession above
			// already guaranteed these are non-null.
			accessExpirationDate = DateTime.fromISO(accessTokenExp as string);
			refreshExpirationDate = DateTime.fromISO(refreshTokenExp as string);
		}

		const now = DateTime.now();

		if (now >= refreshExpirationDate) {
			clearTokens();
			return { allowed: false, redirectUrl };
		}

		if (now < accessExpirationDate) {
			return { allowed: true };
		}

		try {
			const accessExpirationDate = await refreshAccessToken(
				refreshToken as string,
			);
			logger.log(`Token refreshed successfully: '${accessExpirationDate}'`);
			return { allowed: true };
		} catch (err) {
			logger.error(err);
			if (options.clearTokensOnRefreshFailureInAuthEffect) {
				clearTokens();
				return { allowed: false, redirectUrl };
			}
			return { allowed: true };
		}
	}

	function useAuth(): CognitoAuthState {
		const basename = options.useBasename();
		const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';
		const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
		const [isLoading, setIsLoading] = useState(true);

		if (
			options.requireCognitoAuthType &&
			config?.app?.auth?.type !== 'cognito'
		) {
			return { isAuthenticated, isLoading };
		}

		const redirectUri = [
			location.protocol,
			'//',
			location.host,
			basename,
		].join('');
		const redirectUrl = authorizeUrl(redirectUri);

		useEffect(() => {
			checkAuth(isAdmin, isAuthenticated, redirectUrl).then((result) => {
				if (!result.allowed) {
					location.href = result.redirectUrl;
				}
				setIsLoading(false);
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isAdmin, isAuthenticated, redirectUrl]);

		return { isAuthenticated: isAdmin ? true : isAuthenticated, isLoading };
	}

	/**
	 * `beforeLoad` guard for router-level route protection. No-ops entirely
	 * (allows the route to load, does nothing else) unless
	 * `requireAuthEnabled` is off or `config.app.auth.enabled` is true —
	 * see that option's doc comment for why that gate matters.
	 */
	async function ensureAuthenticated(basename: string): Promise<void> {
		if (options.requireAuthEnabled && !config?.app?.auth?.enabled) {
			return;
		}

		const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';
		const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

		if (options.requireCognitoAuthType && config?.app?.auth?.type !== 'cognito') {
			return;
		}

		const redirectUri = [
			location.protocol,
			'//',
			location.host,
			basename,
		].join('');
		const redirectUrl = authorizeUrl(redirectUri);

		const result = await checkAuth(isAdmin, isAuthenticated, redirectUrl);
		if (!result.allowed) {
			throw redirect({ href: result.redirectUrl });
		}
	}

	function tryRefreshToken(redirect: string, durationOffset?: DurationLike) {
		const isAuthenticated = localStorage.getItem(AUTH_KEY);
		const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
		if (isAdmin || !isAuthenticated) {
			return;
		}

		const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		const accessTokenExp = localStorage.getItem(ACCESS_TOKEN_EXPIRATION_KEY);
		const refreshTokenExp = localStorage.getItem(
			REFRESH_TOKEN_EXPIRATION_KEY,
		);

		if (!navigator.onLine) {
			return;
		}

		const missingSession = options.requireExpirationKeysPresent
			? !accessToken || !refreshToken || !accessTokenExp || !refreshTokenExp
			: !refreshToken || !refreshTokenExp;

		if (missingSession) {
			logger.debug(`The refresh token is missing`);
			if (options.tryRefreshTokenClearsOnInvalidSession) {
				clearTokens(redirect);
			}
			return;
		}

		const refreshExpirationDate = DateTime.fromISO(refreshTokenExp as string);
		const now = DateTime.now();

		// magshop's original checked refresh-expiration before access-token
		// validity; basic's checked the opposite order. Provably inert for
		// basic (neither branch has a side effect there — both
		// tryRefreshTokenClearsOnInvalidSession/tryRefreshTokenPerformsRefresh
		// are false), so this order is used for both rather than forking it.
		if (now >= refreshExpirationDate) {
			logger.error(`The refresh token is expired`);
			if (options.tryRefreshTokenClearsOnInvalidSession) {
				clearTokens(redirect);
			}
			return;
		}

		if (accessToken && accessTokenExp) {
			const accessExpirationDate = durationOffset
				? DateTime.fromISO(accessTokenExp).minus(durationOffset)
				: DateTime.fromISO(accessTokenExp);
			if (now < accessExpirationDate) {
				const diff = accessExpirationDate.diff(now);
				logger.debug(
					`The token is valid and is going to expire in `,
					msToTime(diff.toObject().milliseconds),
				);
				return;
			}
		}

		if (!options.tryRefreshTokenPerformsRefresh) {
			return;
		}

		refreshAccessToken(refreshToken as string)
			.then((accessExpirationDate) => {
				const diff = DateTime.fromJSDate(new Date(accessExpirationDate)).diff(
					DateTime.now(),
				);
				logger.log(`Token refreshed successfully`);
				logger.debug(`Token is going to expire at: ${accessExpirationDate}`);
				logger.debug(
					`The token is going to expire in: ${msToTime(
						diff.toObject().milliseconds,
					)}`,
				);
			})
			.catch((err) => {
				logger.error(err);
				if (options.tryRefreshTokenClearsOnInvalidSession) {
					clearTokens(redirect);
				}
			});
	}

	function logOut(basename: string) {
		const isAuthenticated = localStorage.getItem(AUTH_KEY);
		if (!isAuthenticated) {
			return;
		}

		const redirectUri = [
			location.protocol,
			'//',
			location.host,
			basename,
		].join('');

		const redirectUrl = options.buildLogoutUrl(redirectUri);
		if (!redirectUrl) {
			return;
		}

		location.href = redirectUrl;
	}

	return {
		useAuth,
		tryRefreshToken,
		logOut,
		refreshAccessToken,
		ensureAuthenticated,
	};
}

interface CognitoAuthState {
	isLoading: boolean;
	isAuthenticated: boolean;
}

type CognitoAuthCheckResult =
	| { allowed: true }
	| { allowed: false; redirectUrl: string };
